import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  IconButton,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import InventoryIcon from '@mui/icons-material/Inventory';
import EngineeringIcon from '@mui/icons-material/Engineering';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import BoltIcon from '@mui/icons-material/Bolt';
import BuildIcon from '@mui/icons-material/Build';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import SettingsIcon from '@mui/icons-material/Settings';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import FactoryIcon from '@mui/icons-material/Factory';
import CellTowerIcon from '@mui/icons-material/CellTower';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import { colors } from '../theme';

const Landing: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  // Hero slider content
  const slides = [
    {
      background: '/electrical-background.jpg',
      title: 'Get Your Process Under Control',
      subtitle: 'We design efficient, custom systems built for businesses driven by electrical control panels.',
      buttonText: 'Discover the Difference',
      buttonLink: '/about',
    },
    {
      background: '/slide-2-bg.jpg',
      title: 'IP54, IP65 and IP67 Field Power Supplies',
      subtitle: 'The decentralized FIEPOS power supplies with high protection classes were developed for flexible use directly in the field.',
      buttonText: 'Learn More',
      buttonLink: '/products',
    },
    {
      background: '/slide-3-bg.jpg',
      title: 'Chemical Storage Tanks',
      subtitle: 'RPM-Process Control offers the largest product selection of High-Density Linear and Crosslink Polyethylene tanks in the industry.',
      buttonText: 'Learn More',
      buttonLink: '/products/chemical-storage-tanks',
    },
  ];

  // Services
  const services = [
    { title: 'New Product Selection', description: 'Expert guidance in choosing the right products for your specific application needs.', link: '/services', icon: <InventoryIcon /> },
    { title: 'Application Assistance', description: 'Technical support to help you implement and optimize your control systems.', link: '/services', icon: <EngineeringIcon /> },
    { title: 'Product Technology Migration', description: 'Seamless upgrades from legacy systems to modern control solutions.', link: '/services', icon: <SyncAltIcon /> },
    { title: 'VFD Commissioning Support', description: 'Professional setup and configuration of variable frequency drives.', link: '/services', icon: <BoltIcon /> },
    { title: 'VFD Retrofit Consulting', description: 'Upgrade existing installations with the latest VFD technology.', link: '/services', icon: <BuildIcon /> },
  ];

  // Products
  const products = [
    {
      title: 'VFD Panels',
      description: 'Variable Frequency Drive Control Panels designed to withstand voltage surges and changing climate conditions.',
      image: '/variable-frequency-drive-panels.jpg',
      link: '/products/vfd-panels',
    },
    {
      title: 'Chemical Storage Tanks',
      description: 'The largest product selection of high-density linear and crosslink polyethylene tanks in the industry.',
      image: '/tanks.jpg',
      link: '/products/chemical-storage-tanks',
    },
    {
      title: 'Combination Starter Panels',
      description: 'Enclosing a motor starter and electrical protection devices together for efficient operation.',
      image: '/starter.jpg',
      link: '/products/combination-starter-panels',
    },
    {
      title: 'Electrical Control Product',
      description: 'A comprehensive range of electrical control products from leading manufacturers.',
      image: '/electrical.jpg',
      link: '/products/electrical-control-product',
    },
  ];

  // Markets
  const markets = [
    { name: 'Water & Wastewater', icon: <WaterDropIcon /> },
    { name: 'Pump Systems', icon: <SettingsIcon /> },
    { name: 'Hospitals', icon: <LocalHospitalIcon /> },
    { name: 'Chemical Process Plants', icon: <FactoryIcon /> },
    { name: 'Wireless Telemetry', icon: <CellTowerIcon /> },
    { name: "OEM's", icon: <PrecisionManufacturingIcon /> },
  ];

  // Manufacturers
  const manufacturers = [
    { name: 'Transcoil', logo: '/transcoil.jpg', link: '/suppliers' },
    { name: 'Danfoss', logo: '/danfoss.jpg', link: '/suppliers' },
    { name: 'Puls', logo: '/Puls.jpg', link: '/suppliers' },
    { name: 'Pfannenberg', logo: '/pfannenberg.jpg', link: '/suppliers' },
    { name: 'Macromatic', logo: '/Macromatic.jpg', link: '/suppliers' },
  ];

  // Auto-advance slider
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 7000);
    return () => clearInterval(timer);
  }, [slides.length]);

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % slides.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);

  return (
    <Box sx={{ overflow: 'hidden' }}>
      {/* Hero Slider Section */}
      <Box
        sx={{
          minHeight: { xs: '70vh', md: '85vh' },
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          backgroundImage: `url(${slides[currentSlide].background})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          transition: 'background-image 0.8s ease-in-out',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(45, 45, 45, 0.85) 0%, rgba(61, 61, 61, 0.7) 100%)',
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box
            sx={{
              maxWidth: 700,
              animation: 'slideUp 0.8s ease-out',
            }}
          >
            <Chip
              label="Process Control Solutions"
              sx={{
                mb: 3,
                backgroundColor: 'rgba(245, 166, 35, 0.2)',
                color: colors.primary,
                fontWeight: 600,
                fontSize: '0.85rem',
                border: `1px solid ${colors.primary}`,
              }}
            />
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '2.25rem', sm: '3rem', md: '3.5rem' },
                lineHeight: 1.15,
                mb: 3,
                color: '#FFFFFF',
                fontWeight: 700,
              }}
            >
              {slides[currentSlide].title}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: 'rgba(255, 255, 255, 0.85)',
                fontSize: { xs: '1.1rem', md: '1.25rem' },
                mb: 4,
                lineHeight: 1.7,
                maxWidth: 550,
              }}
            >
              {slides[currentSlide].subtitle}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                component={Link}
                to={slides[currentSlide].buttonLink}
                variant="contained"
                size="large"
                endIcon={<ArrowForwardIcon />}
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: '1rem',
                }}
              >
                {slides[currentSlide].buttonText}
              </Button>
              <Button
                component={Link}
                to="/contact"
                variant="outlined"
                size="large"
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: '1rem',
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                  color: '#FFFFFF',
                  '&:hover': {
                    borderColor: '#FFFFFF',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                Get a Quote
              </Button>
            </Box>
          </Box>
        </Container>

        {/* Slider Navigation */}
        <IconButton
          onClick={prevSlide}
          sx={{
            position: 'absolute',
            left: { xs: 16, md: 32 },
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#FFFFFF',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            '&:hover': {
              backgroundColor: 'rgba(245, 166, 35, 0.3)',
            },
          }}
        >
          <ArrowBackIosNewIcon />
        </IconButton>
        <IconButton
          onClick={nextSlide}
          sx={{
            position: 'absolute',
            right: { xs: 16, md: 32 },
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#FFFFFF',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            '&:hover': {
              backgroundColor: 'rgba(245, 166, 35, 0.3)',
            },
          }}
        >
          <ArrowForwardIosIcon />
        </IconButton>

        {/* Slide Indicators */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 40,
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: 1.5,
          }}
        >
          {slides.map((_, index) => (
            <Box
              key={index}
              onClick={() => setCurrentSlide(index)}
              sx={{
                width: currentSlide === index ? 32 : 12,
                height: 12,
                borderRadius: 6,
                backgroundColor: currentSlide === index ? colors.primary : 'rgba(255, 255, 255, 0.4)',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
              }}
            />
          ))}
        </Box>
      </Box>

      {/* Services Section */}
      <Box
        sx={{
          py: { xs: 5, md: 6 },
          background: '#FFFFFF',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 4,
            background: `linear-gradient(90deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`,
          },
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography
              variant="overline"
              sx={{ color: colors.primary, fontWeight: 600, letterSpacing: 2, fontSize: '0.75rem' }}
            >
              What We Offer
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '1.75rem', md: '2rem' },
                color: colors.secondary,
                mt: 0.5,
              }}
            >
              Services: <Box component="span" sx={{ color: colors.primary }}>How Can We Help You?</Box>
            </Typography>
          </Box>

          <Grid container spacing={3} sx={{ mb: 4 }}>
            {services.map((service, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Box
                  component={Link}
                  to={service.link}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 2,
                    p: 2.5,
                    textDecoration: 'none',
                    background: '#FFFFFF',
                    borderRadius: 2,
                    border: `1px solid ${colors.border}`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: colors.primary,
                      boxShadow: `0 4px 20px rgba(245, 166, 35, 0.15)`,
                      transform: 'translateY(-2px)',
                      '& .service-icon': {
                        background: colors.primary,
                        color: '#FFFFFF',
                      },
                    },
                  }}
                >
                  <Box
                    className="service-icon"
                    sx={{
                      flexShrink: 0,
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      background: colors.background,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: colors.primary,
                      transition: 'all 0.3s ease',
                      '& svg': { fontSize: '1.5rem' },
                    }}
                  >
                    {service.icon}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        color: colors.secondary,
                        fontWeight: 600,
                        fontSize: '0.95rem',
                        mb: 0.5,
                      }}
                    >
                      {service.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: colors.textSecondary,
                        fontSize: '0.85rem',
                        lineHeight: 1.5,
                      }}
                    >
                      {service.description}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ textAlign: 'center' }}>
            <Button
              component={Link}
              to="/contact"
              variant="contained"
            >
              Request a Quote
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Process Control Section */}
      <Box
        sx={{
          py: { xs: 8, md: 10 },
          background: colors.background,
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  position: 'relative',
                  borderRadius: 4,
                  overflow: 'hidden',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: 20,
                    left: 20,
                    right: -20,
                    bottom: -20,
                    border: `3px solid ${colors.primary}`,
                    borderRadius: 4,
                    zIndex: -1,
                  },
                }}
              >
                <Box
                  component="img"
                  src="/Electrical-Control-Panels.jpeg"
                  alt="Electrical Control Panels"
                  sx={{
                    width: '100%',
                    borderRadius: 4,
                    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography
                variant="overline"
                sx={{ color: colors.primary, fontWeight: 600, letterSpacing: 2 }}
              >
                About Us
              </Typography>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: '2rem', md: '2.5rem' },
                  color: colors.secondary,
                  mb: 2,
                  mt: 1,
                }}
              >
                Process Control
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: colors.textPrimary,
                  mb: 2,
                  fontWeight: 600,
                }}
              >
                Designing a wide variety of electrical control panels for customers in various industries
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: colors.textSecondary,
                  mb: 4,
                }}
              >
                Process Control specializes in variable frequency drive (VFD) repackaging, pump-control panels, fan-wall panels, starter panels, and all types of process equipment sales. Additionally, we also offer onsite retrofits, upgrades services, and chemical storage solutions.
              </Typography>
              <Button
                component={Link}
                to="/process-control"
                variant="contained"
                endIcon={<ArrowForwardIcon />}
              >
                Learn More
              </Button>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Our Products Section */}
      <Box
        sx={{
          py: { xs: 8, md: 10 },
          background: '#FFFFFF',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="overline"
              sx={{ color: colors.primary, fontWeight: 600, letterSpacing: 2 }}
            >
              What We Provide
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '2rem', md: '2.5rem' },
                color: colors.secondary,
                mt: 1,
              }}
            >
              Our Products
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {products.map((product, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card
                  component={Link}
                  to={product.link}
                  sx={{
                    height: '100%',
                    textDecoration: 'none',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 3,
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: colors.border,
                    '&:hover': {
                      borderColor: colors.primary,
                      '& img': {
                        transform: 'scale(1.05)',
                      },
                    },
                  }}
                >
                  <Box sx={{ overflow: 'hidden', height: 200 }}>
                    <Box
                      component="img"
                      src={product.image}
                      alt={product.title}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transition: 'transform 0.4s ease',
                      }}
                    />
                  </Box>
                  <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        color: colors.secondary,
                        mb: 1,
                        fontWeight: 600,
                      }}
                    >
                      {product.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: colors.textSecondary,
                        lineHeight: 1.6,
                      }}
                    >
                      {product.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ textAlign: 'center', mt: 5 }}>
            <Button
              component={Link}
              to="/products"
              variant="outlined"
              color="secondary"
              endIcon={<ArrowForwardIcon />}
            >
              View All Products
            </Button>
          </Box>
        </Container>
      </Box>

      {/* RPM Suppliers Section */}
      <Box
        sx={{
          py: { xs: 8, md: 10 },
          backgroundImage: 'url(/suppliers-bg.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(45, 45, 45, 0.9) 0%, rgba(61, 61, 61, 0.8) 100%)',
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant="overline"
                sx={{ color: colors.primary, fontWeight: 600, letterSpacing: 2 }}
              >
                Our Partners
              </Typography>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: '2rem', md: '2.5rem' },
                  color: '#FFFFFF',
                  mb: 2,
                  mt: 1,
                }}
              >
                RPM Suppliers
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: 'rgba(255, 255, 255, 0.85)',
                  mb: 4,
                  fontSize: '1.1rem',
                }}
              >
                We offer a wide range of process equipment. You can count on us to always be consistent and reliable when it comes to new equipment sales and equipment upgrades.
              </Typography>
              <Button
                component={Link}
                to="/suppliers"
                variant="contained"
                endIcon={<ArrowForwardIcon />}
              >
                Meet Our Suppliers
              </Button>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  p: 4,
                  borderRadius: 4,
                  background: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(10px)',
                }}
              >
                <Grid container spacing={3} justifyContent="center" alignItems="center">
                  {manufacturers.slice(0, 4).map((manufacturer, index) => (
                    <Grid item xs={6} key={index}>
                      <Box
                        component={Link}
                        to={manufacturer.link}
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          p: 2,
                          borderRadius: 2,
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            backgroundColor: 'rgba(245, 166, 35, 0.1)',
                          },
                        }}
                      >
                        <Box
                          component="img"
                          src={manufacturer.logo}
                          alt={manufacturer.name}
                          sx={{
                            maxWidth: '100%',
                            height: 'auto',
                            maxHeight: 60,
                            filter: 'grayscale(30%)',
                            transition: 'filter 0.3s ease',
                            '&:hover': {
                              filter: 'grayscale(0%)',
                            },
                          }}
                        />
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Markets We Serve Section */}
      <Box
        sx={{
          py: { xs: 8, md: 10 },
          background: colors.secondary,
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="overline"
              sx={{ color: colors.primary, fontWeight: 600, letterSpacing: 2 }}
            >
              Industries
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '2rem', md: '2.5rem' },
                color: '#FFFFFF',
                mt: 1,
              }}
            >
              Markets We Serve
            </Typography>
          </Box>

          <Grid container spacing={3} justifyContent="center">
            {markets.map((market, index) => (
              <Grid item xs={6} sm={4} md={2} key={index}>
                <Box
                  sx={{
                    textAlign: 'center',
                    p: 3,
                    borderRadius: 3,
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      background: 'rgba(245, 166, 35, 0.15)',
                      borderColor: colors.primary,
                      transform: 'translateY(-4px)',
                    },
                  }}
                >
                  <Box sx={{ color: colors.primary, mb: 1, '& svg': { fontSize: '2rem' } }}>
                    {market.icon}
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#FFFFFF',
                      fontWeight: 500,
                    }}
                  >
                    {market.name}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Manufacturers Section */}
      <Box
        sx={{
          py: { xs: 8, md: 10 },
          background: colors.background,
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="overline"
              sx={{ color: colors.primary, fontWeight: 600, letterSpacing: 2 }}
            >
              Trusted Brands
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '1.75rem', md: '2.25rem' },
                color: colors.secondary,
                mt: 1,
              }}
            >
              Manufacturers We Proudly Represent
            </Typography>
          </Box>

          <Grid container spacing={4} justifyContent="center" alignItems="center">
            {manufacturers.map((manufacturer, index) => (
              <Grid item xs={6} sm={4} md={2.4} key={index}>
                <Box
                  component={Link}
                  to={manufacturer.link}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    p: 3,
                    borderRadius: 3,
                    background: '#FFFFFF',
                    border: '1px solid',
                    borderColor: colors.border,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: colors.primary,
                      boxShadow: '0 8px 30px rgba(245, 166, 35, 0.15)',
                      transform: 'translateY(-4px)',
                    },
                  }}
                >
                  <Box
                    component="img"
                    src={manufacturer.logo}
                    alt={manufacturer.name}
                    sx={{
                      maxWidth: '100%',
                      height: 'auto',
                      maxHeight: 60,
                      filter: 'grayscale(20%)',
                      transition: 'filter 0.3s ease',
                      '&:hover': {
                        filter: 'grayscale(0%)',
                      },
                    }}
                  />
                </Box>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ textAlign: 'center', mt: 5 }}>
            <Button
              component={Link}
              to="/suppliers"
              variant="contained"
              endIcon={<ArrowForwardIcon />}
            >
              View All Suppliers
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Landing;
